import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule, CurrencyPipe, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CostReport, EditableField } from '../../interfaces/cost-report';

@Component({
  selector: 'app-financial-report',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './financial-report.component.html',
  styleUrl: './financial-report.component.css'
})
export class FinancialReportComponent implements OnInit, OnChanges {
  @Input() costReport!: CostReport;

  editableIndex: Map<string, EditableField> = new Map();

  constructor() {}

  ngOnInit(): void {
    this.initializeReport();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['costReport'] && !changes['costReport'].firstChange) {
      this.initializeReport();
    }
  }

  private initializeReport(): void {
    this.ensureTablesAndFields();
    this.buildEditableIndex();
    this.recomputeCostReport();
  }

  ensureTablesAndFields(): void {
    if (!this.costReport) return;

    if (!this.costReport.editable_fields || this.costReport.editable_fields.length === 0) {
      this.costReport.editable_fields = [
        { path: 'purchase.price_brl_cents', label: 'Preço de compra', type: 'money', default: 0 },
        { path: 'purchase.auctioneer_fee_pct', label: 'Comissão leiloeiro (%)', type: 'percent', default: 0.05 },
        { path: 'purchase.other_fees_brl_cents', label: 'Outras taxas', type: 'money', default: 0 },
        { path: 'taxes_and_cartorio.itbi_pct', label: 'ITBI (%)', type: 'percent', default: 0.04 },
        { path: 'taxes_and_cartorio.registry_brl_cents', label: 'Registro', type: 'money', default: 0 },
        { path: 'taxes_and_cartorio.deed_brl_cents', label: 'Escritura', type: 'money', default: 0 },
        { path: 'sale_projection.sale_price_gross_brl_cents', label: 'Venda bruta', type: 'money', default: 0 },
        { path: 'sale_projection.brokerage_pct', label: 'Comissão corretagem (%)', type: 'percent', default: 0 },
        { path: 'sale_projection.sale_costs_brl_cents', label: 'Outros custos de venda', type: 'money', default: 0 },
        { path: 'sale_projection.taxes_on_gain_brl_cents', label: 'Imposto sobre ganho', type: 'money', default: 0 }
      ];
    }

    if (!this.costReport.tables || this.costReport.tables.length === 0) {
      this.costReport.tables = [
        {
          title: 'Custos de aquisição estimados',
          description: 'Custos informados ou estimados pelo usuário para fins de simulação.',
          rows: [
            { label: 'Preço de arrematação estimado ', path: 'purchase.price_brl_cents', format: 'money' },
            { label: 'Comissão de leilão estimada', path: 'purchase.auctioneer_fee_pct', format: 'percent' },
            { label: 'Outras despesas de aquisição estimadas', path: 'purchase.other_fees_brl_cents', format: 'money' }
          ]
        },
        {
          title: 'Tributos e despesas cartorárias estimados',
          description: 'Valores podem variar conforme município, tipo de aquisição e legislação aplicável.',
          rows: [
            { label: 'ITBI estimado', path: 'taxes_and_cartorio.itbi_pct', format: 'percent' },
            { label: 'Registro de Imóveis estimado', path: 'taxes_and_cartorio.registry_brl_cents', format: 'money' },
            { label: 'Escritura pública estimada (quando aplicável)', path: 'taxes_and_cartorio.deed_brl_cents', format: 'money' }
          ]
        },
        {
          title: 'Projeção de revenda estimada',
          description: '',
          rows: [
            { label: 'Valor bruno de venda estimado', path: 'sale_projection.sale_price_gross_brl_cents', format: 'money' },
            { label: 'Comissão corretagem (%)', path: 'sale_projection.brokerage_pct', format: 'percent' },
            { label: 'Outros custos de venda estimados ', path: 'sale_projection.sale_costs_brl_cents', format: 'money' },
            { label: 'Tributos sobre a venda estimados', path: 'sale_projection.taxes_on_gain_brl_cents', format: 'money' },
            { label: 'Venda líquida estimada', path: 'sale_projection.sale_price_net_brl_cents', format: 'money' }
          ]
        }
      ];
    }
  }

  buildEditableIndex(): void {
    this.editableIndex.clear();
    if (this.costReport?.editable_fields) {
      for (const f of this.costReport.editable_fields) {
        this.editableIndex.set(f.path, f);
      }
    }
  }

  isEditable(path: string): boolean {
    return this.editableIndex.has(path);
  }

  onCostFieldChange(path: string, rawValue: any): void {
    const field = this.editableIndex.get(path);
    if (!field) return;

    const val = this.parseByType(rawValue, field.type);
    this.setByPath(this.costReport, path, val);
    this.recomputeCostReport();
  }

  getValue(path: string): any {
    const val = this.getByPath(this.costReport, path);
    const field = this.editableIndex.get(path);
    if (field?.type === 'money' && typeof val === 'number') {
      return val / 100;
    }
    return val;
  }

  display(path: string, format: string): string {
    const val = this.getByPath(this.costReport, path);
    if (val === null || val === undefined) return '—';

    if (format === 'money') return this.formatMoneyBRL(val);
    if (format === 'percent') return this.formatPercent(val);

    if (format === 'real') {
      const c = this.costReport;
      const p = c.purchase;
      const price = this.num(p.price_brl_cents);

      if (path === 'purchase.auctioneer_fee_pct') {
        return this.formatMoneyBRL(Math.round(price * val));
      }
      if (path === 'taxes_and_cartorio.itbi_pct') {
        return this.formatMoneyBRL(Math.round(price * val));
      }
      if (path === 'sale_projection.brokerage_pct') {
        const gross = this.num(c.sale_projection.sale_price_gross_brl_cents);
        return this.formatMoneyBRL(Math.round(gross * val));
      }
      return '—';
    }

    return String(val);
  }

  recomputeCostReport(): void {
    const c = this.costReport;

    const p = c.purchase;
    const price = this.num(p.price_brl_cents);
    const feePct = this.num(p.auctioneer_fee_pct);

    const effectiveFee = Math.round(price * feePct);
    c.computed.auctioneer_fee_effective_brl_cents = effectiveFee;

    const otherFees = this.num(p.other_fees_brl_cents);
    const purchaseSubtotal = price + effectiveFee + otherFees;
    c.computed.purchase_subtotal_brl_cents = purchaseSubtotal;

    const t = c.taxes_and_cartorio;
    const itbi = Math.round(price * this.num(t.itbi_pct));
    const registry = this.num(t.registry_brl_cents);
    const deed = this.num(t.deed_brl_cents);

    const totalCost = purchaseSubtotal + itbi + registry + deed;
    c.computed.total_cost_brl_cents = totalCost;

    const s = c.sale_projection;
    const gross = this.num(s.sale_price_gross_brl_cents);
    const brokeragePct = this.num(s.brokerage_pct);
    const brokerage = Math.round(gross * brokeragePct);
    const saleCosts = this.num(s.sale_costs_brl_cents);
    const taxGain = this.num(s.taxes_on_gain_brl_cents);

    const net = gross - brokerage - saleCosts - taxGain;
    s.sale_price_net_brl_cents = net;

    const profit = net - totalCost;
    c.computed.profit_brl_cents = profit;
    c.computed.roi = totalCost > 0 ? profit / totalCost : 0;
  }

  parseByType(raw: any, t: string): any {
    if (raw === null || raw === '') return null;
    const s = String(raw).trim();
    if (t === 'money') {
      const clean = s.replace(/[^\d,.-]/g, '').replace(',', '.');
      const val = parseFloat(clean);
      return isNaN(val) ? null : Math.round(val * 100);
    }
    if (t === 'percent') {
      const clean = s.replace('%', '').replace(',', '.');
      let val = parseFloat(clean);
      if (isNaN(val)) return null;
      if (s.includes('%') || val > 1) {
        val = val / 100;
      }
      return val;
    }
    if (t === 'int') {
      const val = parseInt(s, 10);
      return isNaN(val) ? null : val;
    }
    return raw;
  }

  formatMoneyBRL(cents: number): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100);
  }

  formatPercent(v: number): string {
    return new Intl.NumberFormat('pt-BR', { style: 'percent', minimumFractionDigits: 1 }).format(v);
  }

  num(v: any): number {
    return this.toNumberSafe(v);
  }

  toNumberSafe(v: any): number {
    if (v === null || v === undefined) return 0;
    if (typeof v === 'number') return v;
    const n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  }

  getByPath(obj: any, path: string): any {
    const parts = path.split('.');
    let curr = obj;
    for (const p of parts) {
      if (!curr) return undefined;
      curr = curr[p];
    }
    return curr;
  }

  setByPath(obj: any, path: string, value: any): void {
    const parts = path.split('.');
    let curr = obj;
    for (let i = 0; i < parts.length - 1; i++) {
      const p = parts[i];
      if (!curr[p]) curr[p] = {};
      curr = curr[p];
    }
    curr[parts[parts.length - 1]] = value;
  }
}
